import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import {
  pushNotificationTypes,
  getNotifications,
  subscribe,
  addNotification,
  cancelNotification,
  duplicateNotification,
  consumeFromPlan,
  returnToPlan,
  getPlanConsumption,
  moduleLabels,
  moduleIcons,
  moduleColors,
  audienceCounts,
  type UnifiedNotification,
  type PushNotificationType,
} from '@/mocks/adminNotificationEngine';
import NotificationFlowModal from './components/NotificationFlowModal';
import type { NotificationFlowContext } from './components/NotificationFlowModal';

function timeLabel(iso: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Ayer';
  if (days < 7) return `Hace ${days} días`;
  return new Date(iso).toLocaleDateString('es-GT', { day: 'numeric', month: 'short' });
}

function formatScheduled(iso: string): string {
  return new Date(iso).toLocaleDateString('es-GT', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AdminNotifications() {
  const [consumption, setConsumption] = useState(getPlanConsumption());
  const [filterTab, setFilterTab] = useState<'todos' | 'enviado' | 'programado' | 'borrador'>('todos');
  const [detailRecord, setDetailRecord] = useState<UnifiedNotification | null>(null);
  const [flowModal, setFlowModal] = useState<{ open: boolean; context: NotificationFlowContext }>({
    open: false,
    context: { type: 'transmision', relatedModule: 'manual' },
  });

  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    return subscribe(() => {
      setRefreshKey((k) => k + 1);
      setConsumption(getPlanConsumption());
    });
  }, []);

  const notifications = useMemo(() => getNotifications(), [refreshKey]);

  const consumedPct = Math.round((consumption.usedThisMonth / consumption.planTotal) * 100);
  const remainingPct = 100 - consumedPct;

  const filteredHistory = useMemo(() => {
    if (filterTab === 'todos') return notifications;
    if (filterTab === 'enviado') return notifications.filter((n) => n.status === 'sent');
    if (filterTab === 'programado') return notifications.filter((n) => n.status === 'scheduled');
    if (filterTab === 'borrador') return notifications.filter((n) => n.status === 'draft');
    return notifications;
  }, [notifications, filterTab]);

  const openFlowModal = useCallback((type: PushNotificationType, relatedModule?: NotificationFlowContext['relatedModule'], item?: { id: number; name: string }) => {
    setFlowModal({
      open: true,
      context: {
        type,
        relatedModule: relatedModule || 'manual',
        relatedItemId: item?.id,
        relatedItemName: item?.name,
      },
    });
  }, []);

  const closeFlowModal = useCallback(() => {
    setFlowModal((prev) => ({ ...prev, open: false }));
    setConsumption(getPlanConsumption());
  }, []);

  const handleCancelSchedule = useCallback((record: UnifiedNotification) => {
    SwalAlert.confirm(
      '¿Cancelar notificación?',
      `La notificación "${record.title}" pasará a estado Cancelado.`,
    ).then((result) => {
      if (result.isConfirmed) {
        cancelNotification(record.id);
        if (record.status === 'scheduled' || record.status === 'sent') {
          returnToPlan();
          setConsumption(getPlanConsumption());
        }
        SwalAlert.toast('Notificación cancelada.', 'success');
      }
    });
  }, []);

  const handleResend = useCallback((record: UnifiedNotification) => {
    if (consumption.remaining <= 0) {
      SwalAlert.warning('Límite alcanzado', 'No tenés notificaciones disponibles este mes.');
      return;
    }
    SwalAlert.confirm(
      '¿Reenviar notificación?',
      `Se enviará nuevamente "${record.title}".`,
    ).then((result) => {
      if (result.isConfirmed) {
        consumeFromPlan();
        addNotification({
          type: record.type,
          title: record.title,
          message: record.message,
          status: 'sent',
          channel: record.channel,
          audience: record.audience,
          language: record.language,
          relatedModule: record.relatedModule,
          relatedItemId: record.relatedItemId,
          relatedItemName: record.relatedItemName,
          sentAt: new Date().toISOString(),
          scheduledFor: null,
          recipients: audienceCounts[record.audience],
          delivered: 0,
          opened: 0,
        });
        setConsumption(getPlanConsumption());
        SwalAlert.toast('Notificación reenviada.', 'success');
      }
    });
  }, [consumption.remaining]);

  const handleDuplicate = useCallback((record: UnifiedNotification) => {
    duplicateNotification(record.id);
    SwalAlert.toast('Notificación duplicada como borrador.', 'success');
  }, []);

  const counts = useMemo(() => ({
    todos: notifications.length,
    enviado: notifications.filter((n) => n.status === 'sent').length,
    programado: notifications.filter((n) => n.status === 'scheduled').length,
    borrador: notifications.filter((n) => n.status === 'draft').length,
  }), [notifications]);

  const resetDate = getPlanConsumption().resetsAt;

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5 pl-12">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-notification-3-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Notificaciones</h1>
              <p className="text-foreground-400 text-[9px]">{consumption.remaining} disponibles</p>
            </div>
          </div>
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            Volver
          </Link>
        </div>
      </header>

      <div className="flex">
        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl bg-accent-100 flex items-center justify-center">
                <i className="ri-notification-3-line text-accent-600 text-base"></i>
              </div>
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Notificaciones</h2>
                <p className="text-foreground-400 text-xs">
                  Plan {consumption.planName} &bull; {consumption.remaining} de {consumption.planTotal} disponibles
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8">
            <div className="max-w-[1100px] mx-auto space-y-6">

              {/* ── BACKEND PENDING INDICATOR ── */}
              <div className="bg-amber-50 border border-amber-200/70 rounded-xl px-4 py-2.5 flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-md bg-amber-200 flex items-center justify-center shrink-0">
                  <i className="ri-information-line text-amber-600 text-[10px]"></i>
                </div>
                <p className="text-amber-700 text-[11px] leading-relaxed">
                  Frontend mock. Las notificaciones se guardan solo durante la sesión. La persistencia, envío real por email/WhatsApp, adjuntos y programación real requerirán integración con backend (Cursor).
                </p>
              </div>

              {/* ── PLAN CONSUMPTION ── */}
              <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <i className="ri-flashlight-line text-amber-500 text-sm"></i>
                      <h3 className="text-foreground-950 font-bold text-sm">Consumo del plan</h3>
                      <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-[10px] font-bold">{consumption.planName}</span>
                    </div>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-2xl font-black text-foreground-950">{consumption.usedThisMonth}</span>
                      <span className="text-foreground-400 text-xs">de {consumption.planTotal} usadas este mes</span>
                    </div>
                    <div className="w-full h-3 rounded-full bg-background-200 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${consumedPct}%`,
                          background: consumedPct > 90
                            ? 'linear-gradient(90deg, #ef4444, #f97316)'
                            : consumedPct > 70
                              ? 'linear-gradient(90deg, #f59e0b, #f97316)'
                              : 'linear-gradient(90deg, #10b981, #14b8a6)',
                        }}
                      ></div>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-foreground-400 text-[10px]">{consumedPct}% consumido</span>
                      <span className="text-foreground-400 text-[10px]">Se reinicia el {new Date(resetDate).toLocaleDateString('es-GT', { day: 'numeric', month: 'long' })}</span>
                    </div>
                  </div>

                  <div className="shrink-0 flex flex-col items-center">
                    <div className="relative w-[88px] h-[88px]">
                      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" className="text-background-200" strokeWidth="8" />
                        <circle
                          cx="50" cy="50" r="42" fill="none"
                          stroke="currentColor"
                          className="text-emerald-500"
                          strokeWidth="8"
                          strokeLinecap="round"
                          strokeDasharray={`${2 * Math.PI * 42}`}
                          strokeDashoffset={`${2 * Math.PI * 42 * (1 - remainingPct / 100)}`}
                          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xl font-black text-foreground-950">{consumption.remaining}</span>
                        <span className="text-[9px] text-foreground-400 leading-tight">disponibles</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── NOTIFICATION TYPES ── */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-foreground-950 font-bold text-sm">Tipos de notificación</h3>
                    <p className="text-foreground-400 text-xs">Seleccioná el tipo y creá tu push</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {pushNotificationTypes.map((typeMeta) => (
                    <div
                      key={typeMeta.type}
                      className="bg-background-50 rounded-2xl border border-background-200 p-4 flex flex-col hover:border-background-300 transition-colors group"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-xl ${typeMeta.bgColor} flex items-center justify-center shrink-0`}>
                          <i className={`${typeMeta.icon} ${typeMeta.color} text-lg`}></i>
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-foreground-950 font-bold text-sm whitespace-nowrap">{typeMeta.label}</h4>
                        </div>
                      </div>

                      <p className="text-foreground-500 text-[11px] leading-relaxed mb-4 line-clamp-2 flex-1">
                        {typeMeta.description}
                      </p>

                      <div className="flex items-center gap-2 mt-auto">
                        <button
                          onClick={() => openFlowModal(typeMeta.type)}
                          disabled={consumption.remaining <= 0}
                          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${
                            consumption.remaining <= 0
                              ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                              : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                          }`}
                        >
                          <i className="ri-add-line text-xs"></i>
                          Crear notificación
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {consumption.remaining <= 0 && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-200 flex items-center justify-center shrink-0">
                      <i className="ri-alert-line text-amber-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-amber-800 font-bold text-xs mb-0.5">Límite del plan alcanzado</h4>
                      <p className="text-amber-700 text-[11px] leading-relaxed">
                        Ya usaste las {consumption.planTotal} notificaciones de tu plan {consumption.planName}. El contador se reinicia el {new Date(resetDate).toLocaleDateString('es-GT', { day: 'numeric', month: 'long' })}.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* ── HISTORY ── */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-foreground-950 font-bold text-sm">Historial de notificaciones</h3>
                    <p className="text-foreground-400 text-xs">{counts.todos} notificaciones en total</p>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1 scrollbar-none">
                  {(['todos', 'enviado', 'programado', 'borrador'] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setFilterTab(tab)}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        filterTab === tab
                          ? 'bg-foreground-950 text-background-50'
                          : 'bg-background-50 text-foreground-500 hover:bg-background-200'
                      }`}
                    >
                      {tab === 'todos' && `Todos (${counts.todos})`}
                      {tab === 'enviado' && `Enviados (${counts.enviado})`}
                      {tab === 'programado' && `Programados (${counts.programado})`}
                      {tab === 'borrador' && `Borradores (${counts.borrador})`}
                    </button>
                  ))}
                </div>

                {filteredHistory.length === 0 ? (
                  <div className="text-center py-12 bg-background-50 rounded-2xl border border-background-200">
                    <div className="w-12 h-12 mx-auto rounded-xl bg-background-200 flex items-center justify-center mb-3">
                      <i className="ri-mail-send-line text-foreground-400 text-lg"></i>
                    </div>
                    <p className="text-foreground-500 text-xs">No hay notificaciones {filterTab === 'enviado' ? 'enviadas' : filterTab === 'programado' ? 'programadas' : filterTab === 'borrador' ? 'en borrador' : 'aún'}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredHistory.map((record) => {
                      const typeMeta = pushNotificationTypes.find((t) => t.type === record.type)!;
                      const modColor = moduleColors[record.relatedModule];
                      return (
                        <div
                          key={record.id}
                          onClick={() => setDetailRecord(record)}
                          className="bg-background-50 rounded-2xl border border-background-200 hover:border-background-300 transition-colors cursor-pointer p-4"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-9 h-9 rounded-xl ${typeMeta.bgColor} flex items-center justify-center shrink-0`}>
                              <i className={`${typeMeta.icon} ${typeMeta.color} text-sm`}></i>
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${typeMeta.bgColor} ${typeMeta.color}`}>
                                  {typeMeta.label}
                                </span>

                                {/* Module badge */}
                                {record.relatedModule !== 'manual' && (
                                  <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${modColor.bg} ${modColor.text}`}>
                                    <i className={`${moduleIcons[record.relatedModule]} text-[8px]`}></i>
                                    {moduleLabels[record.relatedModule]}
                                  </span>
                                )}

                                <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${
                                  record.status === 'sent' ? 'bg-emerald-100 text-emerald-600'
                                    : record.status === 'scheduled' ? 'bg-amber-100 text-amber-700'
                                    : record.status === 'sending' ? 'bg-sky-100 text-sky-600'
                                    : record.status === 'draft' ? 'bg-foreground-100 text-foreground-500'
                                    : record.status === 'cancelled' ? 'bg-red-100 text-red-500'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {record.status === 'sent' && 'Enviado'}
                                  {record.status === 'scheduled' && 'Programado'}
                                  {record.status === 'sending' && 'Enviando'}
                                  {record.status === 'draft' && 'Borrador'}
                                  {record.status === 'cancelled' && 'Cancelado'}
                                  {record.status === 'failed' && 'Fallido'}
                                </span>

                                {record.relatedItemName && (
                                  <span className="text-foreground-400 text-[10px] truncate max-w-[140px]">{record.relatedItemName}</span>
                                )}

                                <span className="text-foreground-400 text-[10px] ml-auto whitespace-nowrap">
                                  {record.status === 'scheduled' && record.scheduledFor
                                    ? formatScheduled(record.scheduledFor)
                                    : record.sentAt
                                      ? timeLabel(record.sentAt)
                                      : record.status === 'draft'
                                        ? timeLabel(record.createdAt)
                                        : ''}
                                </span>
                              </div>

                              <h4 className="text-foreground-950 font-bold text-sm mb-1">{record.title}</h4>
                              <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">{record.message}</p>

                              <div className="flex items-center gap-4 mt-3 flex-wrap">
                                {record.status === 'sent' && (
                                  <>
                                    <span className="flex items-center gap-1 text-foreground-400 text-[10px]">
                                      <i className="ri-user-line text-[10px]"></i>
                                      {record.recipients.toLocaleString()}
                                    </span>
                                    <span className="flex items-center gap-1 text-foreground-400 text-[10px]">
                                      <i className="ri-check-double-line text-[10px]"></i>
                                      {record.delivered.toLocaleString()}
                                    </span>
                                    <span className="flex items-center gap-1 text-foreground-400 text-[10px]">
                                      <i className="ri-eye-line text-[10px]"></i>
                                      {record.opened.toLocaleString()} ({record.delivered > 0 ? Math.round((record.opened / record.delivered) * 100) : 0}%)
                                    </span>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDuplicate(record); }}
                                      className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                                    >
                                      <i className="ri-file-copy-line text-[10px]"></i>
                                      Duplicar
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleResend(record); }}
                                      disabled={consumption.remaining <= 0}
                                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                                        consumption.remaining <= 0
                                          ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                                          : 'bg-accent-50 text-accent-600 hover:bg-accent-100'
                                      }`}
                                    >
                                      <i className="ri-refresh-line text-[10px]"></i>
                                      Reenviar
                                    </button>
                                  </>
                                )}
                                {(record.status === 'scheduled' || record.status === 'draft') && (
                                  <>
                                    {record.status === 'draft' && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleDuplicate(record); }}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                                      >
                                        <i className="ri-file-copy-line text-[10px]"></i>
                                        Duplicar
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleCancelSchedule(record); }}
                                      className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-500 text-[10px] font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                                    >
                                      <i className="ri-close-line text-[10px]"></i>
                                      Cancelar
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ── NOTIFICATION FLOW MODAL ── */}
      <NotificationFlowModal
        open={flowModal.open}
        context={flowModal.context}
        onClose={closeFlowModal}
      />

      {/* ── DETAIL MODAL ── */}
      {detailRecord && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setDetailRecord(null)}
        >
          <div
            className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-lg overflow-hidden max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
              <div className="flex items-center gap-3">
                {(() => {
                  const meta = pushNotificationTypes.find((t) => t.type === detailRecord.type)!;
                  const modColor = moduleColors[detailRecord.relatedModule];
                  return (
                    <div className="flex items-center gap-2">
                      <div className={`w-9 h-9 rounded-xl ${meta.bgColor} flex items-center justify-center`}>
                        <i className={`${meta.icon} ${meta.color} text-base`}></i>
                      </div>
                      {detailRecord.relatedModule !== 'manual' && (
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${modColor.bg} ${modColor.text}`}>
                          <i className={`${moduleIcons[detailRecord.relatedModule]} text-[9px]`}></i>
                          {moduleLabels[detailRecord.relatedModule]}
                        </span>
                      )}
                    </div>
                  );
                })()}
                <div>
                  <h3 className="text-foreground-950 font-bold text-sm">Detalle del envío</h3>
                  <p className="text-foreground-400 text-[10px]">
                    {detailRecord.status === 'scheduled' && detailRecord.scheduledFor
                      ? `Programado para ${formatScheduled(detailRecord.scheduledFor)}`
                      : detailRecord.sentAt
                        ? `Enviado ${timeLabel(detailRecord.sentAt)}`
                        : detailRecord.status === 'draft'
                          ? `Creado ${timeLabel(detailRecord.createdAt)}`
                          : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setDetailRecord(null)}
                className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto">
              <div className="flex items-center gap-2 flex-wrap">
                {(() => {
                  const meta = pushNotificationTypes.find((t) => t.type === detailRecord.type)!;
                  return (
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${meta.bgColor} ${meta.color}`}>
                      {meta.label}
                    </span>
                  );
                })()}
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${
                  detailRecord.status === 'sent' ? 'bg-emerald-100 text-emerald-600'
                    : detailRecord.status === 'scheduled' ? 'bg-amber-100 text-amber-700'
                    : detailRecord.status === 'draft' ? 'bg-foreground-100 text-foreground-500'
                    : detailRecord.status === 'cancelled' ? 'bg-red-100 text-red-500'
                    : 'bg-red-100 text-red-600'
                }`}>
                  {detailRecord.status === 'sent' ? 'Enviado' : detailRecord.status === 'scheduled' ? 'Programado' : detailRecord.status === 'draft' ? 'Borrador' : detailRecord.status === 'cancelled' ? 'Cancelado' : detailRecord.status}
                </span>
                {detailRecord.relatedItemName && (
                  <span className="text-foreground-400 text-[10px]">{detailRecord.relatedItemName}</span>
                )}
              </div>

              <h3 className="text-foreground-950 font-bold text-base">{detailRecord.title}</h3>
              <p className="text-foreground-600 text-sm leading-relaxed">{detailRecord.message}</p>

              <div className="bg-background-100 rounded-xl p-4 grid grid-cols-2 gap-3">
                <div>
                  <span className="text-foreground-400 text-[10px]">Canal</span>
                  <p className="text-foreground-800 font-bold text-sm capitalize">{detailRecord.channel}</p>
                </div>
                <div>
                  <span className="text-foreground-400 text-[10px]">Audiencia</span>
                  <p className="text-foreground-800 font-bold text-sm">{detailRecord.audience === 'todos' ? 'Todos' : detailRecord.audience === 'oyentes_activos' ? 'Activos' : detailRecord.audience === 'seguidores' ? 'Seguidores' : 'VIP'}</p>
                </div>
                <div>
                  <span className="text-foreground-400 text-[10px]">Idioma</span>
                  <p className="text-foreground-800 font-bold text-sm">{detailRecord.language.toUpperCase()}</p>
                </div>
                <div>
                  <span className="text-foreground-400 text-[10px]">Destinatarios</span>
                  <p className="text-foreground-800 font-bold text-sm">{detailRecord.recipients.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-background-200 shrink-0">
              {detailRecord.status === 'scheduled' && (
                <button
                  onClick={() => { handleCancelSchedule(detailRecord); setDetailRecord(null); }}
                  className="px-4 py-2 rounded-xl bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
              )}
              {detailRecord.status === 'sent' && (
                <button
                  onClick={() => { handleResend(detailRecord); setDetailRecord(null); }}
                  disabled={consumption.remaining <= 0}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${
                    consumption.remaining <= 0
                      ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                      : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                  }`}
                >
                  Reenviar
                </button>
              )}
              {(detailRecord.status === 'draft' || detailRecord.status === 'sent' || detailRecord.status === 'scheduled') && (
                <button
                  onClick={() => { handleDuplicate(detailRecord); setDetailRecord(null); }}
                  className="px-4 py-2 rounded-xl bg-background-100 text-foreground-500 text-xs font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Duplicar
                </button>
              )}
              <button
                onClick={() => setDetailRecord(null)}
                className="px-4 py-2 rounded-xl bg-background-100 text-foreground-500 text-xs font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}